import React, { useMemo, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Stack,
  FormControl,
  Snackbar,
  Select,
  Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { useSelector, useDispatch } from "react-redux";
import { t } from "../../utils/i18n";
import { closeContactModal } from "../../store/modalSlice";
import { useLocation } from "react-router-dom";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 24,
    maxWidth: "90%",
    width: "95%",
    [theme.breakpoints.down('sm')]: {
      width: "95%",
      maxWidth: "95%",
      margin: "0 8px",
    },
    maxHeight: "90vh",
    overflow: "auto",
    backgroundImage: theme.palette.background.dialog,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: (theme) => theme.palette.background.contactField,
    border: "1px solid rgba(0, 0, 0, 0.1)",
    "& fieldset": {
      borderColor: "transparent",
    },
    "&:hover fieldset": {
      borderColor: "rgba(0, 0, 0, 0.1)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#000",
    },
  },
  "& .MuiInputBase-input": {
    color: (theme) => theme.palette.background.globalButton,
    fontSize: "14px",
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.background.globalButton,
  color: theme.palette.text.third,
  borderRadius: "20px",
  px: "24px",
  py: "12px",
  fontSize: "16px",
  fontWeight: "600",
  textTransform: "none",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: theme.palette.background.globalButtonHover,
  },
  "&:disabled": {
    opacity: 0.6,
  },
  minWidth: "100px",
  height: "40px",
}));
const FormContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  marginTop: "20px",
  maxWidth: "90%",
  mx: "auto",
}));
const ContactModal = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector((state) => state.modal.contactModalOpen);
  const offer_type = useSelector((state) => state.modal.offer_type);
  const lang = useSelector((state) => state.i18n.lang);
  const plansState = useSelector((state) => state.productData.plans);
  const isAr = lang === "ar";
  const [value, setValue] = useState(offer_type);
  const location = useLocation(); 
  const isContactPage = location.pathname === "/contact";
  const [ticketInfo, setTicketInfo] = useState(null); 

  useEffect(() => {
    setValue(offer_type);
  }, [offer_type]);

  const [form, setForm] = useState({
      name: "",
      organization: "",
      email: "",
      phone: "",
      plan: "",
      connectType: "",
      subject: "",
      message: "",
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const handleClose = () => {

    setTicketInfo(null);

    setForm({
      name: "",
      organization: "",
      email: "",
      phone: "",
      plan: "",
      connectType: "",
      subject: "",
      message: "",
    });

    // Close the modal
    dispatch(closeContactModal());
  };


  const plans = useMemo(() => {
  const raw = Array.isArray(plansState.data) ? plansState.data : [];
  const mapped = raw.map((p) => ({
    title: (lang === "ar" ? p?.name_ar : p?.name) || "",
  }));


  if (mapped.length === 0) {
    return [
      { title: "Basic" },
      { title: "Standard" },
      { title: "Premium" },
    ];
  }

  return mapped;
}, [plansState, lang]);


  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const submitFormModal = async () => {

    const { name, organization, email, phone, plan, connectType, subject, message } = form;
    if (!name || !organization || !email || !phone || !plan || !connectType || !subject || !message) {
      setSnackbar({
        open: true,
        severity: "error",
        message: isAr
          ? "يرجى تعبئة جميع التفاصيل"
          : "Please fill all the details",
      });
      return; 
    }
    setSnackbar({
      open: true,
      severity: "info",       
      message: t(lang, "contact.sending_message"),
    });
    try {
      const body = {
        helpdesk_ticket: {
          issue: {
            project_id: "crm",
            subject: form.subject || "Meeting Request",
            description: form.message || "ticket description",
            tracker_id: 31, 
            custom_field_values: {
              "1": "Any", 
              "2": window.location.origin || "https://cyber.naahel.com",
              "42": form.subject || "Subject",
              "83": form.message || "Desc",
              "287": window.location.origin || "https://cyber.naahel.com", // Domain
              "291": form.message || "Event",
              "290": form.connectType, 
            },
          },
          contact: {
            email: form.email,
            phone: form.phone,
            first_name: form.name,
            middle_name: "",
            last_name: "", 
            company: form.organization,
            website: null,
            job_title: null,
            background: null,
            custom_field_values: {
              "18": "", 
              "59": "Lead", 
              "133": "",
              "288": "", 
            },
          },
          send_as: "auto_answer",
        },
      };

  
        const res = await fetch("https://track.dolfprojects.com/customapis/helpdesk_tickets.json", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Redmine-API-Key": process.env.REACT_APP_REDMINE_API_KEY,
        },
        body: JSON.stringify(body),
      });
  
      if (!res.ok) throw new Error("Failed to send");
      const data = await res.json();

      setTicketInfo({
        id: data.helpdesk_ticket.id,
        content: data.helpdesk_ticket.content,
        contactName: data.helpdesk_ticket.contact?.name,
      });
  
      setSnackbar({
        open: true,
        severity: "success",
        message: t(lang, "contact.messsage_text"),
      });
  
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        severity: "error",
        message: t(lang, "contact.failed_text"),
      });
    }
  };

  return (
    <>
    <StyledDialog
      open={isOpen}
      onClose={handleClose}
      TransitionProps={{
        timeout: 300,
      }}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      {ticketInfo ? (
    <DialogContent
        sx={{
          textAlign: "center",
          py: 6,
          px: { xs: 3, sm: 6 },
          backgroundColor: "#f9fafb",
          borderRadius: "16px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2.5,
        }}
      >
        {/* Success Icon */}
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            backgroundColor: "#4caf50",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mb: 2,
          }}
        >
          <CheckIcon sx={{ color: "#fff", fontSize: 40 }} />
        </Box>

        {/* Heading */}
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: "#333", mb: 1 }}
        >
          {t(lang, "contact.ticket_submitted", "Your Ticket is Submitted!")}
        </Typography>

        <Typography
          variant="body2"
          sx={{ color: "#555", mb: 3 }}
        >
          {t(
            lang,
            "contact.ticket_confirmation_text",
            "Thank you! Your ticket has been successfully created. Here are the details:"
          )}
        </Typography>

        {/* Ticket Details */}
        <Box
          sx={{
            width: "100%",
            maxWidth: 400,
            bgcolor: "#fff",
            borderRadius: "12px",
            p: 3,
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            textAlign: "left",
          }}
        >
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>{t(lang, "contact.ticket_id", "Ticket ID")}:</strong>{" "}
            {ticketInfo.id}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>{t(lang, "contact.content", "Content")}:</strong>{" "}
            {ticketInfo.content}
          </Typography>
          <Typography variant="body1">
            <strong>{t(lang, "contact.contact", "Contact")}:</strong>{" "}
            {ticketInfo.contactName}
          </Typography>
        </Box>

        {/* Close Button */}
        <Box mt={4}>
          <SubmitButton
            onClick={() => {
              if (isContactPage) {
                window.location.href = "https://dolftech.com";
                
              } else {
                handleClose(); 
              }
            }}
            sx={{
              px: 5,
              py: 1.5,
              borderRadius: "30px",
              fontWeight: 600,
              textTransform: "none",
              backgroundColor: "#1976d2",
              "&:hover": { backgroundColor: "#1565c0" },
            }}
          >
            {t(lang, "contact.close", "Close")}
          </SubmitButton>
        </Box>
      </DialogContent>

  ) : (
    <>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              opacity: 0.8,
              fontWeight: 500,
              fontSize: "1.2rem",
            }}
          >
            {t(lang, "contact.title")}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: "2rem",
            }}
          >
            {t(lang, "contact.sub-title", "Contact Us")}
          </Typography>
        </Box>
        {!isContactPage && (
            <IconButton onClick={handleClose} sx={{ color: "#000" }}>
              <CloseIcon />
            </IconButton>
          )}
      </DialogTitle>

      <DialogContent
        sx={{
          pt: 2,
          "&::-webkit-scrollbar": {
            width: "10px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#fff",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#000000ff",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#555",
          },
        }}
      >
        <FormContainer maxWidth={"100% !important"}>
          <Box>
            {/* First Row: Institution Name & Name */}
            <Stack
              flexDirection={{ xs: "column", sm: "row" }}
              gap={{ xs: 2, sm: 0 }}
              justifyContent={"space-between"}
              alignItems={"center"}
              mb={3}
            >
              <TextField
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.background.contactField,
                  color: "#000",
                  width: { xs: "100%", sm: "49.5%" },
                  borderRadius: 4,
                  "& fieldset": { border: "none" },
                  "& input::placeholder": {
                    color: (theme) => theme.palette.background.globalButton,
                    opacity: 1,
                  },
                }}
                placeholder={t(lang, "contact.name")}
                id="name"
                value={form.name}
                onChange={(e) => updateField("name", e.target.value)}
              />

              <TextField
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.background.contactField,
                  color: "#000",
                  width: { xs: "100%", sm: "49.5%" },
                  borderRadius: 4,
                  "& fieldset": { border: "none" },
                  "& input::placeholder": {
                    color: (theme) => theme.palette.background.globalButton,
                    opacity: 1,
                  },
                }}
                placeholder={t(lang, "contact.organization")}
                id="organization"
                value={form.organization}
                onChange={(e) => updateField("organization", e.target.value)}
              />
            </Stack>

            <Stack
              flexDirection={{ xs: "column", sm: "row" }}
              gap={{ xs: 2, sm: 0 }}
              justifyContent={"space-between"}
              alignItems={"center"}
              mb={3}
            >
              <TextField
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.background.contactField,
                  color: "#000",
                  width: { xs: "100%", sm: "49.5%" },
                  borderRadius: 4,
                  "& fieldset": { border: "none" },
                  "& input::placeholder": {
                    color: (theme) => theme.palette.background.globalButton,
                    opacity: 1,
                  },
                }}
                placeholder={t(lang, "contact.email")}
                id="email"
                value={form.email}
                onChange={(e) => updateField("email", e.target.value)}
              />

              <TextField
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.background.contactField,
                  color: "#000",
                  width: { xs: "100%", sm: "49.5%" },
                  borderRadius: 4,
                  "& fieldset": { border: "none" },
                  "& input::placeholder": {
                    color: (theme) => theme.palette.background.globalButton,
                    opacity: 1,
                  },
                  "& input[type=number]::-webkit-outer-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                  "& input[type=number]::-webkit-inner-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                  "& input[type=number]": {
                    MozAppearance: "textfield",
                  },
                }}
                type="number"
                placeholder={t(lang, "contact.phone")}
                id="phone"
                value={form.phone}
                onChange={(e) => updateField("phone", e.target.value)}
              />
            </Stack>

            <Stack
              flexDirection={{ xs: "column", sm: "row" }}
              gap={{ xs: 2, sm: 0 }}
              justifyContent={"space-between"}
              alignItems={"center"}
              mb={3}
            >
              <FormControl sx={{ width: { xs: "100%", sm: "49.5%" } }}>
                <Select
                  value={form.plan}
                  onChange={(e) => updateField("plan", e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <span style={{ color: "#999", opacity: 0.7 }}>
                          {t(lang, "contact.request_offer")}
                        </span>
                      );
                    }
                    return selected;
                  }}
                  sx={{
                    width: "100%",
                    backgroundColor: (theme) => theme.palette.background.contactField,
                    borderRadius: "12px",
                    minHeight: "45px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "transparent",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "transparent",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#000",
                    },
                    "& .MuiSelect-select": {
                      color: (theme) => theme.palette.background.globalButton,
                      padding: "12px 14px",
                      minHeight: "45px",
                      fontSize: { xs: "16px", sm: "14px" },
                    },
                  }}
                >
                  {plans.map((plan, index) => (
                    <MenuItem key={index} value={plan.title}>
                      {plan.title}
                    </MenuItem>
                  ))}
                  <MenuItem value={t(lang, "contact.request_offer")}>
                    {t(lang, "contact.request_offer")}
                  </MenuItem>
                </Select>
              </FormControl>

              <TextField
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.background.contactField,
                  color: "#000",
                  width: { xs: "100%", sm: "49.5%" },
                  borderRadius: 4,
                  "& fieldset": { border: "none" },
                  "& input::placeholder": {
                    color: (theme) => theme.palette.background.globalButton,
                    opacity: 1,
                  },
                }}
                placeholder={t(lang, "contact.connectType")}
                id="connectType"
                value={form.connectType}
                onChange={(e) => updateField("connectType", e.target.value)}
              />
            </Stack>

            {/* Fourth Row: Subject */}
            <Stack
              flexDirection={{ xs: "column", sm: "row" }}
              gap={{ xs: 2, sm: 0 }}
              justifyContent={"space-between"}
              alignItems={"center"}
              mb={3}
            >
              <TextField
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.background.contactField,
                  color: "#000",
                  width: "100%",
                  borderRadius: 4,
                  "& fieldset": { border: "none" },
                  "& input::placeholder": {
                    color: (theme) => theme.palette.background.globalButton,
                    opacity: 1,
                  },
                }}
                placeholder={t(lang, "contact.subject")}
                id="subject"
                value={form.subject}
                onChange={(e) => updateField("subject", e.target.value)}
              />
            </Stack>

            {/* Fifth Row: Message */}
            <Stack
              flexDirection={{ xs: "column", sm: "row" }}
              gap={{ xs: 2, sm: 0 }}
              justifyContent={"space-between"}
              alignItems={"center"}
              mb={0}
            >
              <StyledTextField
                fullWidth
                placeholder={t(lang, "contact.message")}
                multiline
                value={form.message}
                onChange={(e) => updateField("message", e.target.value)}
                minRows={6}
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.background.contactField,
                  color: "#000",
                  width: "100%",
                  borderRadius: 4,
                  "& fieldset": { border: "none" },
                  "& textarea::placeholder": {
                    color: (theme) => theme.palette.background.globalButton,
                    opacity: 1,
                  },
                }}
              />
            </Stack>
          </Box>
        </FormContainer>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: isAr ? "flex-start" : "flex-end",
            width: "100%",
          }}
        >
          <SubmitButton variant="contained" onClick={submitFormModal}>
            {t(lang, "contact.send", "Send")}
          </SubmitButton>
        </Box>
      </DialogActions>
      </>
    )}
    </StyledDialog>
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      </>
  );
};

export default ContactModal;
